/**
 * ClaudeDesignDashboard - Production dashboard with real API
 * Uses Claude Design CSS with real event data
 * Routes: /events, /events/:eventId, /events/:eventId/:tab
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { eventService, authService } from '../services/api';
import { OverviewPage, GuestsPage, MessagesPage, InboxPage, FaqPage } from './Dashboard';
import '../styles/claude-design.css';
import '../styles/claude-dashboard.css';

// Helper
const initials = (n) => n ? n.split(' ').map(s => s[0]).join('').slice(0, 2).toUpperCase() : '??';
const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '';
const daysUntil = (d) => d ? Math.ceil((new Date(d) - new Date()) / (1000 * 60 * 60 * 24)) : 0;

// Icons (inline SVG matching Claude Design)
const Icon = ({ name, size = 16 }) => {
  const icons = {
    search: <><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></>,
    check: <><polyline points="20 6 9 17 4 12"/></>,
    sparkles: <><path d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L21 12l-5.813-1.912a2 2 0 0 1-1.275-1.275L12 3Z"/></>,
    message: <><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></>,
    plus: <><path d="M5 12h14M12 5v14"/></>,
    calendar: <><rect width="18" height="18" x="3" y="4" rx="2"/><path d="M16 2v4M8 2v4M3 10h18"/></>,
    sendH: <><path d="m22 2-7 20-4-9-9-4Z"/><path d="M22 2 11 13"/></>,
    heart: <><path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z"/></>,
    user: <><circle cx="12" cy="8" r="5"/><path d="M20 21a8 8 0 0 0-16 0"/></>,
    inbox: <><polyline points="22 12 16 12 14 15 10 15 8 12 2 12"/><path d="M5.45 5.11 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z"/></>,
    faq: <><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><path d="M12 17h.01"/></>,
    logout: <><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></>,
  };
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      {icons[name] || icons.message}
    </svg>
  );
};

// ============ SIDEBAR ============
function Sidebar({ events, currentEventId, currentTab, navigate, user, tierUsage }) {
  const handleEventClick = (eventId) => {
    navigate(`/events/${eventId}/overview`);
  };

  const handleTabClick = (tab) => {
    if (currentEventId) {
      navigate(`/events/${currentEventId}/${tab}`);
    }
  };

  const handleLogout = () => {
    authService.logout();
    navigate('/login');
  };

  const currentEvent = events.find(e => e._id === currentEventId);
  const guestCount = currentEvent?.guests?.length || 0;

  return (
    <div className="sb">
      <div className="sb-brand">
        <div className="sb-mark"><Icon name="heart" size={14} /></div>
        <div className="sb-logo">mandap</div>
      </div>

      <div className="sb-section">
        <div className="sb-eyebrow" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>Events</span>
          {tierUsage && !tierUsage.isAdmin && tierUsage.events.max > 0 && (
            <span style={{ fontSize: 10, letterSpacing: 0, textTransform: 'none', fontWeight: 500 }}>
              {tierUsage.events.used}/{tierUsage.events.max}
            </span>
          )}
        </div>
        {events.map(e => (
          <div
            key={e._id}
            className={`sb-event ${currentEventId === e._id ? 'is-active' : ''}`}
            onClick={() => handleEventClick(e._id)}
          >
            <div className="sb-epic">{initials(e.name)}</div>
            <div>
              <div className="sb-ename">{e.name}</div>
              <div className="sb-edate">{formatDate(e.startDate)}</div>
            </div>
          </div>
        ))}
        {tierUsage && !tierUsage.isAdmin && tierUsage.events.remaining === 0 ? (
          <div className="sb-add" style={{ opacity: 0.5, cursor: 'default', borderColor: 'var(--c-border)' }}
            title={`${tierUsage.tier} plan limit: ${tierUsage.events.max} event${tierUsage.events.max !== 1 ? 's' : ''}`}>
            <Icon name="plus" size={14} /> Plan limit reached
          </div>
        ) : (
          <button className="sb-add" onClick={() => navigate('/create-event')}>
            <Icon name="plus" size={14} /> New event
          </button>
        )}
      </div>

      {currentEventId && (
        <div className="sb-section">
          <div className="sb-eyebrow">Workspace</div>
          {[
            { id: 'overview', icon: 'calendar', label: 'Overview' },
            { id: 'guests', icon: 'user', label: 'Guests', badge: guestCount > 0 ? guestCount : null },
            { id: 'messages', icon: 'sendH', label: 'Messages' },
            { id: 'inbox', icon: 'inbox', label: 'Inbox' },
            { id: 'faq', icon: 'faq', label: 'FAQ' },
          ].map(item => (
            <div
              key={item.id}
              className={`sb-item ${currentTab === item.id ? 'is-active' : ''}`}
              onClick={() => handleTabClick(item.id)}
            >
              <Icon name={item.icon} size={18} />
              {item.label}
              {item.badge && <span className="badge">{item.badge}</span>}
            </div>
          ))}
        </div>
      )}

      <div className="sb-spacer" />
      <div className="sb-user" style={{ cursor: 'pointer' }} onClick={handleLogout} title="Click to logout">
        <div className="sb-user-img">{initials(user?.name || 'User')}</div>
        <div style={{ flex: 1 }}>
          <div className="sb-user-name">{user?.name || 'User'}</div>
          <div className="sb-user-role">Wedding planner</div>
        </div>
        <Icon name="logout" size={16} />
      </div>
    </div>
  );
}

// ============ TOPBAR ============
function Topbar({ title, eyebrow, actions }) {
  return (
    <div className="tb">
      <div>
        <div className="tb-eyebrow">{eyebrow}</div>
        <h1 className="tb-title">{title}</h1>
      </div>
      <div className="tb-search">
        <Icon name="search" size={14} />
        <input placeholder="Search guests, messages..." />
      </div>
      <div className="tb-actions">{actions}</div>
    </div>
  );
}

// ============ NO EVENT SELECTED ============
function NoEventSelected({ events, navigate, tierUsage }) {
  const atLimit = tierUsage && !tierUsage.isAdmin && tierUsage.events.remaining === 0;
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', flexDirection: 'column', gap: 16 }}>
      <Icon name="calendar" size={64} />
      <h2 style={{ margin: 0 }}>Select an event</h2>
      <p style={{ color: 'var(--c-fg-muted)', margin: 0 }}>
        {events.length === 0 ? 'Create your first event to get started' : 'Choose an event from the sidebar'}
      </p>
      {events.length === 0 && !atLimit && (
        <button className="btn-primary" onClick={() => navigate('/create-event')}>
          <Icon name="plus" size={14} /> Create Event
        </button>
      )}
    </div>
  );
}

// ============ LOADING STATE ============
function LoadingState() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
      <div style={{ textAlign: 'center' }}>
        <div className="sb-mark" style={{ width: 48, height: 48, margin: '0 auto 16px' }}>
          <Icon name="heart" size={24} />
        </div>
        <p style={{ color: 'var(--c-fg-muted)' }}>Loading...</p>
      </div>
    </div>
  );
}

// ============ MAIN DASHBOARD ============
function ClaudeDesignDashboard() {
  const { eventId, tab = 'overview' } = useParams();
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [currentEvent, setCurrentEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [tierUsage, setTierUsage] = useState(null);

  // Fetch all events
  const fetchEvents = useCallback(async () => {
    try {
      const data = await eventService.getAllEvents();
      setEvents(data || []);

      // If we have an eventId, fetch its full details
      if (eventId && data?.length > 0) {
        const eventData = await eventService.getEventFullDetails(eventId);
        setCurrentEvent(eventData);
      }
    } catch (error) {
      console.error('Error fetching events:', error);
    } finally {
      setLoading(false);
    }
  }, [eventId]);

  // Fetch user info + tier usage
  useEffect(() => {
    const userData = authService.getCurrentUser();
    setUser(userData);
    authService.getTierUsage()
      .then(data => setTierUsage(data))
      .catch(err => console.error('Error fetching tier usage:', err));
  }, []);

  // Initial load
  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  // Refresh current event when eventId changes
  useEffect(() => {
    if (eventId && events.length > 0) {
      setLoading(true);
      eventService.getEventFullDetails(eventId)
        .then(data => {
          setCurrentEvent(data);
          setLoading(false);
        })
        .catch(err => {
          console.error('Error fetching event details:', err);
          setLoading(false);
        });
    }
  }, [eventId, events.length]);

  const refreshTierUsage = useCallback(() => {
    authService.getTierUsage()
      .then(data => setTierUsage(data))
      .catch(err => console.error('Error refreshing tier usage:', err));
  }, []);

  const handleRefresh = useCallback(() => {
    if (eventId) {
      eventService.getEventFullDetails(eventId)
        .then(data => {
          setCurrentEvent(data);
        });
    }
    refreshTierUsage();
  }, [eventId, refreshTierUsage]);

  // Auto-refresh: poll for data changes every 30s when tab is visible
  const refreshRef = useRef(handleRefresh);
  refreshRef.current = handleRefresh;

  useEffect(() => {
    if (!eventId) return;

    const POLL_INTERVAL = 30000; // 30 seconds
    let intervalId;

    const startPolling = () => {
      intervalId = setInterval(() => {
        if (document.visibilityState === 'visible') {
          refreshRef.current();
        }
      }, POLL_INTERVAL);
    };

    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        // Refresh immediately when user returns to tab
        refreshRef.current();
      }
    };

    startPolling();
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [eventId]);

  // Page meta based on current event
  const getPageMeta = () => {
    if (!currentEvent) {
      return { eyebrow: '', title: 'Dashboard', actions: null };
    }

    const days = daysUntil(currentEvent.startDate);
    const daysText = days > 0 ? `${days} days to go` : days === 0 ? 'Today!' : 'Event passed';
    const guests = currentEvent.guests || [];
    const confirmed = guests.filter(g => g.rsvp === 'yes').length;

    const metas = {
      overview: {
        eyebrow: `${currentEvent.name} · ${daysText}`,
        title: <>Plan with <em>clarity.</em></>,
        actions: <button className="btn-primary" onClick={() => navigate(`/events/${eventId}/guests`)}>View Guests</button>
      },
      guests: {
        eyebrow: `${guests.length} invited · ${confirmed} confirmed`,
        title: 'Guest list',
        actions: null // Actions are in the page component
      },
      messages: {
        eyebrow: 'Compose & schedule',
        title: 'Messages',
        actions: null
      },
      inbox: {
        eyebrow: 'Conversations',
        title: 'Inbox',
        actions: null
      },
      faq: {
        eyebrow: `${currentEvent.faqs?.length || 0} entries`,
        title: <>Frequently <em>asked.</em></>,
        actions: null
      },
    };

    return metas[tab] || metas.overview;
  };

  const meta = getPageMeta();

  // Render the active page
  const renderPage = () => {
    if (loading) return <LoadingState />;
    if (!eventId) return <NoEventSelected events={events} navigate={navigate} tierUsage={tierUsage} />;
    if (!currentEvent) return <LoadingState />;

    const pageProps = { event: currentEvent, onRefresh: handleRefresh, eventId };

    switch (tab) {
      case 'overview': return <OverviewPage {...pageProps} />;
      case 'guests': return <GuestsPage {...pageProps} tierUsage={tierUsage} refreshTierUsage={refreshTierUsage} />;
      case 'messages': return <MessagesPage {...pageProps} />;
      case 'inbox': return <InboxPage {...pageProps} />;
      case 'faq': return <FaqPage {...pageProps} />;
      default: return <OverviewPage {...pageProps} />;
    }
  };

  // Inline style to override App.css conflicts
  const appStyle = {
    display: 'grid',
    gridTemplateColumns: '264px 1fr',
    minHeight: '100vh',
    width: '100vw',
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 9999,
    background: '#fff',
  };

  return (
    <div className="claude-dashboard" style={appStyle}>
      <Sidebar
        events={events}
        currentEventId={eventId}
        currentTab={tab}
        navigate={navigate}
        user={user}
        tierUsage={tierUsage}
      />
      <div className="main">
        {currentEvent && (
          <Topbar
            title={meta.title}
            eyebrow={meta.eyebrow}
            actions={meta.actions}
          />
        )}
        <div className="content" style={!currentEvent ? { display: 'flex', alignItems: 'center', justifyContent: 'center' } : {}}>
          {renderPage()}
        </div>
      </div>
    </div>
  );
}

export default ClaudeDesignDashboard;
